import React, { Suspense } from 'react';
import { QueryClientProvider } from 'react-query';
import ReactDOM from 'react-dom';
import { BrowserRouter, Redirect, Route, Switch } from 'react-router-dom';
import { AnimatedPageLoader } from 'smartsuite-ui';
import DashboardApp from './AppRouter';
import ExportDashboard from './viewDashboards/export/ExportDashboard';
import AppWrapper from './advancedSearch/AppWrapper';
import {
    getApiUri,
    getNodeServerUri,
    getNonApiUri,
} from './viewDashboards/utils/getUri';
import favoriteUtilities from './shared/filtering/favoriteUtils';
import { queryClient } from './advancedSearch/queryUtils';
import { buildSearchUriConstants } from './advancedSearch/initializationUtils';
import './shared/translations/i18n';
import 'smartsuite-ui/dist/smartsuite-ui.css';

export const buildUriConstants = (isNodeServer) => {
    const getUri = isNodeServer ? getNodeServerUri : getApiUri;

    return {
        // User Dashboards will be done later
        getUserDashboardConfigurationUri: (dashboardKey) =>
            getNodeServerUri(
                `DashboardApi/GetUserDashboardConfiguration?dashboardKey=${dashboardKey}`
            ),
        addUserDashboardUri: getNodeServerUri('DashboardApi/AddUserDashboard'),
        editUserDashboardUri: getNodeServerUri(
            'DashboardApi/EditUserDashboard'
        ),
        getDeleteUserDashboardUri: (dashboardKey) =>
            getNodeServerUri(
                `DashboardApi/DeleteUserDashboard?dashboardKey=${dashboardKey}`
            ),

        downloadDashboard: getNodeServerUri('DashboardApi/Download'),
        pdfExportUri: getApiUri(`DashboardApi/PdfExport`),

        getSharedUrl: (id) => getUri(`SharedUrl/GetSharedUrlById?id=${id}`),
        saveSharedUrl: getUri('SharedUrl/SaveSharedUrl'),
        getShareableUri: (externalLink) =>
            `${window.location.origin}${getNonApiUri(externalLink)}`,

        logMessageUri: getUri('Logging/LogMessage'),

        pageHelpUri: (onlineHelpId) =>
            `http://test-help/Default.htm#cshid=${onlineHelpId}`,

        getNonApiUri,
        auditLogUri: getUri('AuditLogApi'),
        getSystemManifestUri: getUri('DashboardApi/GetSystemManifest'),
        getSystemLinks: getApiUri('SmartSuiteMenuApi'),
        getUserFeatureTokensUri: getUri('FeatureToken/GetUserFeatureTokens'),
        canModifyLandingPage: getUri('DashboardApi/CanModifyLandingPage'),
        getAllDashboardConfigurations: getUri(
            'DashboardConfiguration/GetAllDashboardConfigurations'
        ),
        getDashboardConfigurationUri: (dashboardKey) =>
            getUri(
                `DashboardConfiguration/GetDashboardConfiguration?dashboardKey=${dashboardKey}`
            ),
        getLandingPageConfigurationUri: getUri(
            'LandingPage/GetLandingPageConfiguration'
        ),
        getAllUserPreferences: getUri(`UserPreferences/GetAllUserPreferences`),
        getUserPreferences: (dashboardKey) =>
            getApiUri(
                `UserPreferences/GetUserPreferencesByKey?key=${dashboardKey}`
            ),

        saveUserPreferences: getApiUri('UserPreferences/SaveUserPreferences'),
        metricDataUri: getUri('DashboardMetric/GetMetricData'),
        selectOptionsUri: getUri('SelectOptions/GetCriteriaValues'),
        thresholdsUri: getUri('DashboardThresholds/GetAllThresholds'),

        // server.js only endpoints
        getUserFavorites: getNodeServerUri('DashboardApi/GetUserFavorites'),
    };
};

function CustomWidget() {
    return <div>Tester 123</div>;
}

function CustomWidgetWrapper() {
    return <CustomWidget />;
}

const customizations = {
    supplementalTableCellRenderer: ({ columnConfig }) => {
        if (columnConfig.renderType === 'test') {
            return <td>YAY!</td>;
        }

        return undefined;
    },
    widgetRenderers: {
        customWidgetTest: {
            key: 'customWidgetTest',
            render: (widgetProps) => <CustomWidgetWrapper {...widgetProps} />,
        },
    },
    buildAdvancedSearchExternalLinkParameter: async () => {
        console.log('buildAdvancedSearchExternalLinkParameter'); // eslint-disable-line
    },
};

ReactDOM.render(
    <Suspense fallback={<AnimatedPageLoader />}>
        <QueryClientProvider client={queryClient}>
            <BrowserRouter basename={window.rootUrl}>
                <Switch>
                    <Route
                        path="/"
                        exact={true}
                        render={() => <Redirect to="Dashboard/LandingPage" />}
                    />
                    <Route path="/Search/:searchKey">
                        <AppWrapper uriConstants={buildSearchUriConstants()} />
                    </Route>
                    <Route
                        path="/PdfExport/Dashboard/:localStorageKey?/:printViewMode?/:siteLanguage?"
                        render={({ match }) => (
                            <ExportDashboard
                                assets={{
                                    statusMapImages: {
                                        dfw: '/images/dfw_map.png',
                                        yyz: '/images/yyz_map.png',
                                    },
                                    smartsuiteLogo:
                                        '/images/SmartSuite_Logo_Full_Colour.svg',
                                    brockLogo:
                                        '/images/logo_BrockSolutions_FullColour.svg',
                                }}
                                localStorageKey={match.params.localStorageKey}
                                printViewMode={match.params.printViewMode}
                                siteLanguage={match.params.siteLanguage}
                                uriConstants={buildUriConstants(
                                    process.env.REACT_APP_NODE_SERVER === 'true'
                                )}
                            />
                        )}
                    />
                    <Route
                        path="/Dashboard"
                        render={() => (
                            <DashboardApp
                                assets={{
                                    statusMapImages: {
                                        dfw: '/images/dfw_map.png',
                                        yyz: '/images/yyz_map.png',
                                    },
                                    smartsuiteLogo:
                                        '/images/SmartSuite_Logo_Full_Colour.svg',
                                    brockLogo:
                                        '/images/logo_BrockSolutions_FullColour.svg',
                                }}
                                baseName={`${window.rootUrl}Dashboard`}
                                customizations={customizations}
                                favoriteUtilities={favoriteUtilities}
                                siteLanguage="en"
                                uriConstants={buildUriConstants(
                                    process.env.REACT_APP_NODE_SERVER === 'true'
                                )}
                                ssiSystemFeature={{
                                    header: true,
                                    footer: true,
                                }}
                            />
                        )}
                    />
                </Switch>
            </BrowserRouter>
        </QueryClientProvider>
    </Suspense>,
    document.getElementById('root')
);